import { gql, useMutation } from '@apollo/client';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import Button from 'components/Button';
import { FormInput, FormPanel } from 'components/FormPanel';

type Division = {
  id: string;
  name: string;
  shortName: string;
  createdAt: string;
  updatedAt: string;
};

type CreateDivisionMutationResponse = {
  createDivision: Division;
};

type CreateDivisionMutationVariables = {
  name: string;
  shortName?: string;
  departmentId: string;
};

const CREATE_DIVISION_MUTATION = gql`
  mutation CreateDivision($name: String!, $shortName: String!, $departmentId: ID!) {
    createDivision(name: $name, shortName: $shortName, departmentId: $departmentId) {
      id
      name
      shortName
      createdAt
      updatedAt
    }
  }
`;

const CreateDivisionDialogButton: FC = () => {
  const [showDialog, toggleDialog] = useState(false);
  const { departmentId } = useParams<{ departmentId: string }>() as { departmentId: string };

  const [createDivision, { loading, error }] = useMutation(CREATE_DIVISION_MUTATION, {
    update(cache, { data }) {
      if (!data) return;
      cache.modify({
        id: `DepartmentType:${departmentId}`,
        fields: {
          divisions(existingDivisionsRef) {
            const newDivisionNodeRef = cache.writeFragment({
              data: data.createDivision,
              fragment: gql`
                fragment NewDivision on DivisionType {
                  id
                  name
                  shortName
                  updatedAt
                  createdAt
                }
              `,
            });
            return [newDivisionNodeRef, ...existingDivisionsRef];
          },
        },
      });
    },
  });

  return (
    <>
      <Button onClick={() => toggleDialog(true)}>Add Division</Button>
      <Dialog open={showDialog} onClose={() => toggleDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add a Division</DialogTitle>
        <DialogContent sx={{ minHeight: '50vh' }}>
          <FormPanel
            loading={loading}
            error={error}
            onCancel={() => toggleDialog(false)}
            onSubmit={data => {
              createDivision({
                variables: {
                  name: data.name,
                  shortName: data.shortName,
                  departmentId: departmentId,
                },
              }).then(() => toggleDialog(false));
            }}
          >
            <FormInput
              fullWidth
              type="string"
              fieldName="name"
              label="Name"
              defaultValue=""
              validators={{
                required: true,
              }}
            />
            <FormInput
              fullWidth
              type="string"
              fieldName="shortName"
              label="Short Name"
              defaultValue=""
            />
          </FormPanel>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateDivisionDialogButton;
